import type { ChangesSinceLastVisit } from '../types'
import type { Audit } from '@app/shared/types/sf-data'

import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { getAudits, getChangesSinceLastVisit } from '../api'
import { SfLog } from '@app/adapters/logger'
import { UIState, UIStateType } from '@app/shared/constants'
import { SilverfinContext } from '@app/shared/types/sf-data'

export const useAuditsStore = defineStore('audits', () => {
  const audits = ref<Audit[]>([])
  const changesSinceLastVisit = ref<ChangesSinceLastVisit>({
    count: 0,
    since: 0,
  })
  const uiStatus = ref<UIStateType>(UIState.DEFAULT)

  const isFetchingAudits = computed(() => uiStatus.value === UIState.LOADING)

  async function fetchAudits({
    contextId,
    contextType,
  }: {
    contextId: number
    contextType: SilverfinContext.Account | SilverfinContext.ReconciliationText
  }): Promise<void> {
    uiStatus.value = UIState.LOADING

    try {
      const data = await getAudits({ contextId, contextType })

      if (!data) {
        throw new Error('Failed to load audits')
      }

      audits.value = data.audits

      if (data?.audits?.length === 0) {
        uiStatus.value = UIState.EMPTY
      } else {
        uiStatus.value = UIState.DEFAULT
      }
    } catch (error) {
      uiStatus.value = UIState.ERROR
      SfLog.info('Failed to load audits', {
        extra: {
          contextId,
          contextType,
          error,
        },
      })
    }
  }

  async function fetchChangesSinceLastVisit({
    contextId,
    contextType,
  }: {
    contextId: number
    contextType: SilverfinContext.Account | SilverfinContext.ReconciliationText
  }): Promise<void> {
    try {
      const data = await getChangesSinceLastVisit({ contextId, contextType })

      if (data?.changesSinceLastVisit) {
        changesSinceLastVisit.value = data.changesSinceLastVisit
      } else {
        changesSinceLastVisit.value = {
          count: 0,
          since: 0,
        }
      }
    } catch (error) {
      SfLog.info('Failed to load changes since last visit', {
        extra: {
          contextId,
          contextType,
          error,
        },
      })
    }
  }

  return {
    audits,
    changesSinceLastVisit,
    fetchAudits,
    fetchChangesSinceLastVisit,
    isFetchingAudits,
    uiStatus,
  }
})
